import { memo } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link as MuiLink,
  IconButton,
  Divider,
} from '@mui/material';
import { Link } from '@tanstack/react-router';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import XIcon from '@mui/icons-material/X';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { SocialLinks } from '@neumind/shared';

interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
  staticPage?: boolean;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const footerSections: FooterSection[] = [
  {
    title: 'Solutions',
    links: [
      { label: 'Professionals', href: '/solutions/professionals' },
      { label: 'Clients & Caregivers', href: '/solutions/clients' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Book Demo', href: '/contact-us' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About Us', href: '/about' },
      { label: 'Contact Us', href: '/contact-us' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Blog', href: '/resources/blog' },
      { label: 'Clinical Evidence', href: '/resources/research' },
      { label: 'Case Studies', href: '/resources/case-studies' },
      { label: 'Support Hub', href: 'https://support.neumind.io', external: true },
    ],
  },
  {
    title: 'Legal',
    links: [
      // /privacy is now a real TanStack route (2026-05-31 legal-pages-back-into-SPA
      // migration), so client-side <Link> works. The old LG-32 staticPage workaround
      // was removed with it.
      { label: 'Privacy Policy', href: '/privacy' },
      // /terms has no SPA route — it CF-301s to /legal/eula per the terraform
      // redirects map. staticPage forces a full-page <a href> so the edge redirect
      // fires (a TanStack <Link to="/terms"> would 404 client-side). LG-32 in
      // ~/notes/neumind/cso/regulatory/handover-v0/known-gaps.md.
      { label: 'Terms of Service', href: '/terms', staticPage: true },
      { label: 'Compliance', href: '/trust' },
    ],
  },
];

const socialLinks = [
  { icon: <FacebookIcon />, href: SocialLinks.facebook, label: 'Facebook' },
  { icon: <InstagramIcon />, href: SocialLinks.instagram, label: 'Instagram' },
  { icon: <XIcon />, href: SocialLinks.twitter, label: 'X' },
  { icon: <LinkedInIcon />, href: SocialLinks.linkedin, label: 'LinkedIn' },
  { icon: <YouTubeIcon />, href: SocialLinks.youtube, label: 'YouTube' },
];

function FooterComponent() {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#f8fafc',
        borderTop: '1px solid #e2e8f0',
        py: 8,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src="https://assets.neumind.io/logo.png"
              alt="Neumind"
              loading="lazy"
              decoding="async"
              width={173}
              height={40}
              sx={{ height: 40, width: 'auto', mb: 2 }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, maxWidth: 280 }}>
              Extend care beyond the clinic with our digital neurorehabilitation platform.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {socialLinks.map((social, index) => (
                <IconButton
                  key={index}
                  component="a"
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {footerSections.map((section) => (
            <Grid item xs={6} sm={3} md={2} key={section.title}>
              <Typography
                variant="subtitle2"
                component="p"
                sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
              >
                {section.title}
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {section.links.map((link) => (
                  <Box component="li" key={link.label} sx={{ mb: 1 }}>
                    {link.external ? (
                      <MuiLink
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' },
                          fontSize: '0.875rem',
                        }}
                      >
                        {link.label}
                      </MuiLink>
                    ) : link.staticPage ? (
                      <MuiLink
                        href={link.href}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' },
                          fontSize: '0.875rem',
                        }}
                      >
                        {link.label}
                      </MuiLink>
                    ) : (
                      <MuiLink
                        component={Link}
                        to={link.href}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' },
                          fontSize: '0.875rem',
                        }}
                      >
                        {link.label}
                      </MuiLink>
                    )}
                  </Box>
                ))}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 4, borderColor: '#e2e8f0' }} />

        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          © {new Date().getFullYear()} neumind. All rights reserved. · ICO registration ZB038508
        </Typography>
      </Container>
    </Box>
  );
}

export const Footer = memo(FooterComponent);
